import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'available' | 'issued' | 'due_for_service' | 'in_service' | 'scrapped';
type Life = 'good' | 'near_end' | 'over' | 'uncounted';

interface ToolRow {
  id: string;
  code: string;
  name: string;
  toolType: string | null;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  location: string | null;
  ownedBy: string | null;
  lifeUnits: number | null;
  warnAtPercent: number;
  usedUnits: number;
  totalUnits: number;
  serviceCount: number;
  maxServices: number | null;
  cost: number;
  status: Status;
  statusNote: string | null;
  notes: string | null;
  life: Life;
  remainingUnits: number | null;
  usedPercent: number | null;
  issuedTo: string | null;
  issuedAt: string | null;
  workOrderNumber: string | null;
  workCenterName: string | null;
}
interface Tool extends ToolRow {
  issues: { id: string; issuedAt: string; issuedTo: string; workOrderNumber: string | null; workCenterName: string | null; returnedAt: string | null; unitsProduced: number | null; conditionOnReturn: string | null; notes: string | null }[];
  services: { id: string; kind: string; startedOn: string; doneBy: string | null; completedOn: string | null; cost: number; unitsBefore: number; lifeRestored: boolean; notes: string | null }[];
  serviceCost: number;
}
interface ToolPage {
  data: ToolRow[];
  total: number;
  totalPages: number;
}
interface Summary {
  available: number;
  issued: number;
  dueForService: number;
  inService: number;
  scrapped: number;
  nearEnd: number;
  serviceCostThisMonth: number;
}
interface Choice {
  id: string;
  name: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  available: { label: 'On the shelf', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  issued: { label: 'Out', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  due_for_service: { label: 'Due for service', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  in_service: { label: 'Being serviced', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  scrapped: { label: 'Scrapped', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const CONDITION: Record<string, string> = { good: 'Good', needs_service: 'Needs service', damaged: 'Damaged' };
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
const listOf = <T,>(result: unknown): T[] => (Array.isArray(result) ? result : ((result as { data?: T[] })?.data ?? [])) as T[];

const toolApi = {
  page: async (params: object): Promise<ToolPage> => (await api.get('/tools', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/tools/summary')).data,
  one: async (id: string): Promise<Tool> => (await api.get(`/tools/${id}`)).data,
  save: async (id: string | null, data: object): Promise<Tool> => (id ? await api.put(`/tools/${id}`, data) : await api.post('/tools', data)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/tools/${id}`);
  },
  act: async (id: string, action: string, data: object): Promise<Tool> => (await api.post(`/tools/${id}/${action}`, data)).data,
  workCenters: async (): Promise<Choice[]> => listOf<Choice>((await api.get('/master-data/work-centers', { params: { limit: 100 } })).data),
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// How much of its life a tool has used, as a bar
const LifeBar: React.FC<{ tool: ToolRow }> = ({ tool }) => {
  if (tool.life === 'uncounted' || tool.lifeUnits === null) return <span className="text-sm text-gray-400">Not counted</span>;
  const percent = tool.usedPercent ?? 0;
  return (
    <div className="min-w-[9rem]">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={cn('tabular-nums', tool.life === 'over' ? 'font-medium text-red-600' : tool.life === 'near_end' ? 'font-medium text-amber-700' : 'text-gray-700')}>{formatNumber(tool.usedUnits)} of {formatNumber(tool.lifeUnits)}</span>
        <span className="text-gray-500 tabular-nums">{formatNumber(percent)}%</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div className={cn('h-full rounded-full', tool.life === 'over' ? 'bg-red-500' : tool.life === 'near_end' ? 'bg-amber-500' : 'bg-green-500')} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- add or change a tool

const ToolForm: React.FC<{ tool: Tool | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ tool, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({
    code: tool?.code ?? '',
    name: tool?.name ?? '',
    toolType: tool?.toolType ?? '',
    location: tool?.location ?? '',
    ownedBy: tool?.ownedBy ?? '',
    lifeUnits: tool?.lifeUnits == null ? '' : String(tool.lifeUnits),
    warnAtPercent: String(tool?.warnAtPercent ?? 80),
    maxServices: tool?.maxServices == null ? '' : String(tool.maxServices),
    cost: tool ? String(tool.cost) : '',
    usedUnits: '',
    notes: tool?.notes ?? '',
  });
  const [product, setProduct] = useState<ProductChoice | null>(tool?.productId ? { id: tool.productId, name: tool.productName ?? '', sku: tool.productSku ?? '' } : null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [] } = useQuery({ queryKey: ['tools', 'products', search], queryFn: () => toolApi.products(search), enabled: search.length >= 2 && !product });
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: field === 'code' ? event.target.value.toUpperCase() : event.target.value }));
    setErrors({});
  };
  const whole = (value: string) => value.trim() === '' || (Number.isInteger(Number(value)) && Number(value) >= 0);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[A-Z0-9][A-Z0-9/-]{1,29}$/.test(form.code.trim())) next.code = '2 to 30 capitals, digits, hyphens or slashes';
    if (form.name.trim().length < 2) next.name = 'Name the tool';
    if (!whole(form.lifeUnits) || (form.lifeUnits.trim() !== '' && Number(form.lifeUnits) < 1)) next.lifeUnits = 'A whole number above zero';
    if (!whole(form.warnAtPercent) || Number(form.warnAtPercent) < 1 || Number(form.warnAtPercent) > 100) next.warnAtPercent = '1 to 100';
    if (!whole(form.maxServices)) next.maxServices = 'A whole number';
    if (!whole(form.usedUnits)) next.usedUnits = 'A whole number';
    if (form.cost.trim() !== '' && (!Number.isFinite(Number(form.cost)) || Number(form.cost) < 0)) next.cost = 'Enter an amount';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const number = (value: string) => (value.trim() === '' ? null : Number(value));
    onSubmit({
      code: form.code.trim(),
      name: form.name.trim(),
      toolType: form.toolType.trim() || null,
      productId: product?.id ?? null,
      location: form.location.trim() || null,
      ownedBy: form.ownedBy.trim() || null,
      lifeUnits: number(form.lifeUnits),
      warnAtPercent: Number(form.warnAtPercent),
      maxServices: number(form.maxServices),
      cost: number(form.cost) ?? 0,
      notes: form.notes.trim() || null,
      ...(tool ? {} : { usedUnits: number(form.usedUnits) }),
    });
  };
  const input = (field: keyof typeof form, label: string, extra: { required?: boolean; placeholder?: string; maxLength?: number; type?: string; className?: string; hint?: string } = {}) => (
    <Field label={label} htmlFor={`tl-${field}`} required={extra.required} error={errors[field]} hint={extra.hint} className={extra.className}>
      <input id={`tl-${field}`} type={extra.type ?? 'text'} maxLength={extra.maxLength} placeholder={extra.placeholder} value={form[field]} onChange={set(field)} className={cn(inputClass, errors[field] && inputErrorClass)} />
    </Field>
  );
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {input('code', 'Code', { required: true, placeholder: 'DIE-042', maxLength: 30, hint: 'The number marked on it' })}
        {input('name', 'Name', { required: true, placeholder: 'Blanking die, bracket 12 mm', maxLength: 100, className: 'sm:col-span-2' })}
        {input('toolType', 'Kind', { placeholder: 'Die, mould, jig, fixture…', maxLength: 60 })}
        {input('location', 'Kept at', { maxLength: 100 })}
        {input('ownedBy', 'Owned by', { maxLength: 100, hint: 'When it is not your own, such as a customer' })}
        {input('lifeUnits', 'Life between services', { type: 'number', hint: 'Pieces or strokes; empty when not counted' })}
        {input('warnAtPercent', 'Flag at % of life', { type: 'number' })}
        {input('maxServices', 'Can be serviced', { type: 'number', hint: 'Times; empty for no limit' })}
        {input('cost', 'Cost ₹', { type: 'number' })}
        {!tool && input('usedUnits', 'Already made', { type: 'number', hint: 'Since its last service' })}
        {input('notes', 'Notes', { maxLength: 500, className: tool ? 'sm:col-span-2' : undefined })}
      </div>
      <Field label="The part it makes" htmlFor="tl-product" hint="Optional">
        {product ? (
          <p className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200">
            <span>{product.name} <span className="text-xs text-gray-500">{product.sku}</span></span>
            <button type="button" onClick={() => setProduct(null)} aria-label="Remove the part" className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"><XMarkIcon className="h-4 w-4" /></button>
          </p>
        ) : (
          <>
            <input id="tl-product" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU" className={inputClass} />
            {search.length >= 2 && found.length > 0 && (
              <ul className="mt-1 max-h-40 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {found.map((choice) => (
                  <li key={choice.id}>
                    <button type="button" onClick={() => { setProduct(choice); setSearchInput(''); setSearch(''); }} className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                      <span className="font-medium text-gray-900">{choice.name}</span> <span className="text-gray-500">{choice.sku}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Field>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : tool ? 'Save Tool' : 'Add Tool'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one tool, and what is done with it

type Mode = 'view' | 'issue' | 'return' | 'service' | 'serviced' | 'scrap';

const ToolPanel: React.FC<{ toolId: string; canAct: boolean; canDelete: boolean; onEdit: (tool: Tool) => void; onClose: () => void; onChanged: () => void }> = ({ toolId, canAct, canDelete, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: tool, isLoading, isError, error } = useQuery({ queryKey: ['tools', 'one', toolId], queryFn: () => toolApi.one(toolId) });
  const { data: workCenters = [] } = useQuery({ queryKey: ['tools', 'work-centers'], queryFn: toolApi.workCenters });
  const [mode, setMode] = useState<Mode>('view');
  const [fields, setFields] = useState({ issuedTo: '', workCenterId: '', units: '', condition: 'good', notes: '', kind: '', doneBy: '', date: today(), cost: '', lifeRestored: true, reason: '' });
  const [formError, setFormError] = useState('');
  const set = (field: keyof typeof fields) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFields((prev) => ({ ...prev, [field]: event.target.value }));
    setFormError('');
  };
  const open = (next: Mode) => {
    setFields({ issuedTo: '', workCenterId: '', units: '', condition: 'good', notes: '', kind: '', doneBy: '', date: today(), cost: '', lifeRestored: true, reason: '' });
    setFormError('');
    setMode(next);
  };
  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data: object }) => toolApi.act(toolId, action, data),
    onSuccess: (saved, { action }) => {
      const message: Record<string, string> = {
        issue: `${saved.code} given out to ${saved.issuedTo}`,
        return: saved.status === 'due_for_service' ? `${saved.code} is back and held for service${saved.statusNote ? `: ${saved.statusNote}` : ''}` : `${saved.code} is back on the shelf`,
        service: `${saved.code} sent for service`,
        'service/complete': saved.status === 'available' ? `${saved.code} is back from service and on the shelf` : `${saved.code} is back from service, still held`,
        scrap: `${saved.code} scrapped`,
      };
      (action === 'return' && saved.status === 'due_for_service' ? toast.error : toast.success)(message[action], { duration: 6000 });
      queryClient.setQueryData(['tools', 'one', toolId], saved);
      setMode('view');
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 }),
  });
  const remove = useMutation({
    mutationFn: () => toolApi.remove(toolId),
    onSuccess: () => {
      toast.success('Tool deleted');
      onChanged();
      onClose();
    },
    onError: (removeError) => toast.error(getApiErrorMessage(removeError, 'The tool could not be deleted'), { duration: 7000 }),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !tool) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The tool could not be loaded.')}</p>;

  const submit = () => {
    if (mode === 'issue') {
      if (fields.issuedTo.trim().length < 2) return setFormError('Say who takes the tool');
      return act.mutate({ action: 'issue', data: { issuedTo: fields.issuedTo.trim(), workCenterId: fields.workCenterId || null, notes: fields.notes.trim() || null } });
    }
    if (mode === 'return') {
      const units = Number(fields.units);
      if (fields.units.trim() === '' || !Number.isInteger(units) || units < 0) return setFormError('Enter how much was made with it, as a whole number');
      if (fields.condition !== 'good' && fields.notes.trim().length < 3) return setFormError(fields.condition === 'damaged' ? 'Say what is damaged' : 'Say what needs doing to the tool');
      return act.mutate({ action: 'return', data: { unitsProduced: units, condition: fields.condition, notes: fields.notes.trim() || null } });
    }
    if (mode === 'service') {
      if (fields.kind.trim().length < 2) return setFormError('Say what is to be done to the tool');
      if (!fields.date || fields.date > today()) return setFormError('Enter a date that is not in the future');
      return act.mutate({ action: 'service', data: { kind: fields.kind.trim(), doneBy: fields.doneBy.trim() || null, startedOn: fields.date, notes: fields.notes.trim() || null } });
    }
    if (mode === 'serviced') {
      const cost = fields.cost.trim() === '' ? 0 : Number(fields.cost);
      if (!Number.isFinite(cost) || cost < 0) return setFormError('Enter what it cost');
      if (!fields.date || fields.date > today()) return setFormError('Enter a date that is not in the future');
      return act.mutate({ action: 'service/complete', data: { completedOn: fields.date, cost, lifeRestored: fields.lifeRestored, notes: fields.notes.trim() || null } });
    }
    if (mode === 'scrap') {
      if (fields.reason.trim().length < 3) return setFormError('Say why the tool is scrapped');
      return act.mutate({ action: 'scrap', data: { reason: fields.reason.trim() } });
    }
  };
  const willBe = tool.lifeUnits !== null && fields.units.trim() !== '' && Number.isFinite(Number(fields.units)) ? tool.usedUnits + Number(fields.units) : null;
  const confirm: Record<Exclude<Mode, 'view'>, string> = { issue: 'Give Out', return: 'Take Back', service: 'Send for Service', serviced: 'Service Done', scrap: 'Confirm Scrapping' };
  const servicesLeft = tool.maxServices === null ? null : Math.max(0, tool.maxServices - tool.serviceCount);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[tool.status].className)}>{STATUS[tool.status].label}</span>
        <span className="font-semibold text-gray-900">{tool.name}</span>
        {tool.toolType && <span>{tool.toolType}</span>}
        {tool.productName && <span>Makes {tool.productName}</span>}
        {tool.location && <span>Kept at {tool.location}</span>}
        {tool.ownedBy && <span>Owned by {tool.ownedBy}</span>}
        {tool.status === 'issued' && <span className="font-medium text-blue-700">With {tool.issuedTo}{tool.workCenterName ? ` at ${tool.workCenterName}` : ''}{tool.workOrderNumber ? ` for ${tool.workOrderNumber}` : ''} since {formatMoment(tool.issuedAt)}</span>}
      </div>
      {tool.statusNote && <p className={cn('rounded-md px-4 py-3 text-sm ring-1', tool.status === 'due_for_service' ? 'bg-red-50 text-red-800 ring-red-600/20' : 'bg-gray-50 text-gray-700 ring-gray-200')}>{tool.status === 'due_for_service' ? 'Cannot be given out until it is serviced. ' : ''}{tool.statusNote}</p>}
      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-4">
        <div className="col-span-2"><p className="text-xs uppercase tracking-wide text-gray-500">Life used since its last service</p><div className="mt-1"><LifeBar tool={tool} /></div>{tool.remainingUnits !== null && <p className="mt-1 text-xs text-gray-500">{formatNumber(tool.remainingUnits)} left · flagged at {tool.warnAtPercent}%</p>}</div>
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Made in all</p><p className="font-medium text-gray-900">{formatNumber(tool.totalUnits)}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Serviced</p><p className="font-medium text-gray-900">{formatNumber(tool.serviceCount)} time(s){servicesLeft !== null ? `, ${formatNumber(servicesLeft)} left` : ''}</p><p className="text-xs text-gray-500">{formatMoney(tool.serviceCost)} spent · cost {formatMoney(tool.cost)}</p></div>
      </div>

      {mode !== 'view' && (
        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          {mode === 'issue' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Given to" htmlFor="ta-to" required><input id="ta-to" type="text" maxLength={100} autoFocus value={fields.issuedTo} onChange={set('issuedTo')} placeholder="Operator or setter" className={inputClass} /></Field>
              <Field label="Work center" htmlFor="ta-center">
                <select id="ta-center" value={fields.workCenterId} onChange={set('workCenterId')} className={inputClass}>
                  <option value="">Not recorded</option>
                  {workCenters.map((center) => (<option key={center.id} value={center.id}>{center.name}</option>))}
                </select>
              </Field>
              <Field label="Notes" htmlFor="ta-notes" className="sm:col-span-2"><input id="ta-notes" type="text" maxLength={300} value={fields.notes} onChange={set('notes')} className={inputClass} /></Field>
            </div>
          )}
          {mode === 'return' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Made with it on this issue" htmlFor="ta-units" required hint={willBe !== null && tool.lifeUnits !== null ? `That makes ${formatNumber(willBe)} of ${formatNumber(tool.lifeUnits)}${willBe >= tool.lifeUnits ? ': at the end of its life, it will be held for service' : ''}` : 'Pieces or strokes'}>
                <input id="ta-units" type="number" min="0" step="1" autoFocus value={fields.units} onChange={set('units')} className={cn(inputClass, 'text-right tabular-nums')} />
              </Field>
              <Field label="State it came back in" htmlFor="ta-condition" required>
                <select id="ta-condition" value={fields.condition} onChange={set('condition')} className={inputClass}>
                  <option value="good">Good: back on the shelf</option>
                  <option value="needs_service">Needs service before it is used again</option>
                  <option value="damaged">Damaged</option>
                </select>
              </Field>
              <Field label={fields.condition === 'good' ? 'Notes' : fields.condition === 'damaged' ? 'What is damaged' : 'What needs doing'} htmlFor="ta-notes" required={fields.condition !== 'good'} className="sm:col-span-2"><input id="ta-notes" type="text" maxLength={300} value={fields.notes} onChange={set('notes')} className={inputClass} /></Field>
            </div>
          )}
          {mode === 'service' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="What is done" htmlFor="ta-kind" required><input id="ta-kind" type="text" maxLength={60} autoFocus list="service-kinds" value={fields.kind} onChange={set('kind')} placeholder="Regrind" className={inputClass} /></Field>
              <Field label="Done by" htmlFor="ta-by" hint="The tool room or a vendor"><input id="ta-by" type="text" maxLength={120} value={fields.doneBy} onChange={set('doneBy')} className={inputClass} /></Field>
              <Field label="Sent on" htmlFor="ta-date" required><input id="ta-date" type="date" max={today()} value={fields.date} onChange={set('date')} className={inputClass} /></Field>
              <Field label="Notes" htmlFor="ta-notes" className="sm:col-span-3"><input id="ta-notes" type="text" maxLength={300} value={fields.notes} onChange={set('notes')} className={inputClass} /></Field>
              <datalist id="service-kinds">{['Regrind', 'Refurbish', 'Repair', 'Polish', 'Re-coat'].map((kind) => (<option key={kind} value={kind} />))}</datalist>
            </div>
          )}
          {mode === 'serviced' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Came back on" htmlFor="ta-date" required><input id="ta-date" type="date" max={today()} value={fields.date} onChange={set('date')} className={inputClass} /></Field>
              <Field label="Cost ₹" htmlFor="ta-cost"><input id="ta-cost" type="number" min="0" step="0.01" value={fields.cost} onChange={set('cost')} className={cn(inputClass, 'text-right tabular-nums')} /></Field>
              <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
                <input id="ta-restored" type="checkbox" checked={fields.lifeRestored} onChange={(event) => setFields((prev) => ({ ...prev, lifeRestored: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Its life starts again from zero
              </label>
              <Field label="Notes" htmlFor="ta-notes" className="sm:col-span-3"><input id="ta-notes" type="text" maxLength={300} value={fields.notes} onChange={set('notes')} className={inputClass} /></Field>
            </div>
          )}
          {mode === 'scrap' && (
            <Field label="Why is the tool scrapped?" htmlFor="ta-reason" required><input id="ta-reason" type="text" maxLength={300} autoFocus value={fields.reason} onChange={set('reason')} className={inputClass} /></Field>
          )}
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode('view')} disabled={act.isPending} className={secondaryButtonClass}>Back</button>
            <button type="button" onClick={submit} disabled={act.isPending} className={mode === 'scrap' ? dangerButtonClass : primaryButtonClass}>{act.isPending ? 'Saving…' : confirm[mode]}</button>
          </div>
        </div>
      )}

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issues · {tool.issues.length}</h4>
        {tool.issues.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">This tool has not been given out yet.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Given out</th>
                  <th scope="col" className={thClass}>To</th>
                  <th scope="col" className={thClass}>Came back</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Made</th>
                  <th scope="col" className={thClass}>State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tool.issues.map((issue) => (
                  <tr key={issue.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{formatMoment(issue.issuedAt)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{issue.issuedTo}<span className="block text-xs text-gray-500">{[issue.workCenterName, issue.workOrderNumber, issue.notes].filter(Boolean).join(' · ')}</span></td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{issue.returnedAt ? formatMoment(issue.returnedAt) : <span className="font-medium text-blue-700">Still out</span>}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-900 tabular-nums">{issue.unitsProduced === null ? '—' : formatNumber(issue.unitsProduced)}</td>
                    <td className={cn('whitespace-nowrap px-4 py-2 text-sm', issue.conditionOnReturn && issue.conditionOnReturn !== 'good' ? 'font-medium text-red-600' : 'text-gray-700')}>{issue.conditionOnReturn ? CONDITION[issue.conditionOnReturn] ?? issue.conditionOnReturn : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {tool.services.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Services · {tool.services.length}</h4>
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {tool.services.map((service) => (
              <li key={service.id} className="flex flex-wrap items-baseline gap-x-4 px-4 py-2">
                <span className="font-medium text-gray-900">{service.kind}</span>
                <span className="min-w-0 flex-1 text-gray-700">{[service.doneBy, service.notes].filter(Boolean).join(' · ') || '—'}</span>
                <span className="text-xs text-gray-500">{formatDate(service.startedOn)} to {service.completedOn ? formatDate(service.completedOn) : 'not back yet'} · after {formatNumber(service.unitsBefore)} made{service.completedOn ? ` · ${formatMoney(service.cost)}${service.lifeRestored ? '' : ' · life not restored'}` : ''}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {mode === 'view' && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {canDelete && tool.issues.length === 0 && tool.services.length === 0 && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>}
          {canAct && (
            <>
              <button type="button" onClick={() => onEdit(tool)} className={secondaryButtonClass}>Edit</button>
              {(tool.status === 'available' || tool.status === 'due_for_service') && <button type="button" onClick={() => open('scrap')} className={secondaryButtonClass}>Scrap</button>}
              {(tool.status === 'available' || tool.status === 'due_for_service') && <button type="button" onClick={() => open('service')} className={tool.status === 'due_for_service' ? primaryButtonClass : secondaryButtonClass}>Send for Service</button>}
              {tool.status === 'in_service' && <button type="button" onClick={() => open('serviced')} className={primaryButtonClass}>Service Done</button>}
              {tool.status === 'available' && <button type="button" onClick={() => open('issue')} className={primaryButtonClass}>Give Out</button>}
              {tool.status === 'issued' && <button type="button" onClick={() => open('return')} className={primaryButtonClass}>Take Back</button>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Tooling: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('manufacturing.create');
  const canAct = can('manufacturing.update');
  const canDelete = can('manufacturing.delete');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [lifeFilter, setLifeFilter] = useState<Life | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tool | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['tools', 'page', { page, limit, search, status: statusFilter, life: lifeFilter }],
    queryFn: () => toolApi.page({ page, limit, search, status: statusFilter, life: lifeFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['tools', 'summary'], queryFn: toolApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['tools', 'one', viewingId], queryFn: () => toolApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['tools', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => toolApi.save(editing?.id ?? null, input),
    onSuccess: (tool) => {
      toast.success(editing ? `${tool.code} saved` : `${tool.code} added`);
      queryClient.setQueryData(['tools', 'one', tool.id], tool);
      refreshLists();
      closeForm();
      setViewingId(tool.id);
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The tool could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter || !!lifeFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'On the shelf', value: formatNumber(summary?.available ?? 0), hint: `${formatNumber(summary?.issued ?? 0)} out on the shop floor` },
    { label: 'Near the end of their life', value: formatNumber(summary?.nearEnd ?? 0), hint: 'Still in use; plan their service', alert: false },
    { label: 'Due for service', value: formatNumber(summary?.dueForService ?? 0), hint: 'Cannot be given out', alert: (summary?.dueForService ?? 0) > 0 },
    { label: 'Being serviced', value: formatNumber(summary?.inService ?? 0), hint: `${formatMoney(summary?.serviceCostThisMonth ?? 0)} spent on services this month` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Tooling</h1>
          <p className="mt-1 text-sm text-gray-500">Dies, moulds, jigs, fixtures and cutting tools: where each one is, what it has made against its life, and its regrinds and repairs.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Tool
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by code, name, kind, location, owner, the part it makes or who has it" aria-label="Search tools" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
            <option value="">All tools</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
          <select value={lifeFilter} onChange={(event) => { setLifeFilter(event.target.value as Life | ''); setPage(1); }} aria-label="Life" className={cn(inputClass, 'lg:w-52')}>
            <option value="">Any life left</option>
            <option value="near_end">Near the end of its life</option>
            <option value="over">At or past its life</option>
            <option value="good">Plenty of life left</option>
            <option value="uncounted">Not counted</option>
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Tool</th>
                <th scope="col" className={thClass}>Makes</th>
                <th scope="col" className={thClass}>Where</th>
                <th scope="col" className={thClass}>Life used</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Serviced</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The tools could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No tool matches' : 'No tools yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Add the dies, moulds, jigs and fixtures that wear with use.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className={cn('hover:bg-gray-50', row.status === 'scrapped' && 'bg-gray-50/60')}>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.code}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{[row.name, row.toolType].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.productName || '—'}{row.ownedBy && <span className="block text-xs text-gray-500">owned by {row.ownedBy}</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.status === 'issued' ? <span className="font-medium text-blue-700">With {row.issuedTo}</span> : row.location || '—'}{row.status === 'issued' && row.workCenterName && <span className="block text-xs text-gray-500">{row.workCenterName}</span>}</td>
                    <td className="px-4 py-3"><LifeBar tool={row} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.serviceCount)}{row.maxServices !== null ? ` of ${formatNumber(row.maxServices)}` : ''}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.code}`}>Open</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.code}` : 'Add Tool') : viewing?.code ?? 'Tool'} size="xl">
        {formOpen ? (
          <ToolForm key={editing?.id ?? 'new'} tool={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <ToolPanel
              key={viewingId}
              toolId={viewingId}
              canAct={canAct}
              canDelete={canDelete}
              onEdit={(tool) => {
                setEditing(tool);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default Tooling;
